import { Icon, IconLike } from "components/Icon/Icon";
import { IconCollection } from "components/Icon/IconCollection";
import { getNewRecordPath } from "components/Layout/Sidebar/components/CreateNew/CreateNew";
import RecordLabel from "components/RecordLabel";
import { parentExecutionIdInitialFilter } from "containers/ExecutionFilter/constants";
import { Bundle, Project, Workspace } from "containers/ExecutionFilter/interfaces";
import {
    executionFilterPerLocationLink,
    getWorkspaceExecutionFilterLink,
} from "containers/ExecutionFilter/utils";
import { LatestListContainer } from "containers/Projects/components/ProjectTree/LatestListContainer";
import { propagateScrollToSidebar } from "containers/Projects/components/ProjectTree/ProjectTree";
import {
    getTooltipOverlay,
    quickAccessExecutionsOpened,
    quickAccessExecutionsPinned,
    quickAccessOpened,
    quickAccessPinned,
} from "containers/Projects/components/ProjectTree/utils";
import { close, setPinned } from "containers/QuickAccess/QuickAccess.service";
import { getUserPermissions } from "containers/Auth/Auth.selectors";
import { hasOrganizationAdminPermission } from "containers/Auth/permissions";
import { ChangedBundleBadge } from "containers/Resources/components/Badges/Badges";
import { isEmpty } from "lodash";
import { useEffect, useRef, useState } from "react";
import { MenuItem, SubMenu } from "react-pro-sidebar";
import { useDispatch, useSelector } from "react-redux";
import { match, useHistory, useLocation } from "react-router-dom";
import { getWorkspaceLogsLink, mapScreensToPaths } from "utils/RouterManager/utils";
import { isContainerNavigationLinkActive } from "utils/RouterManager/subPageLinks";
import { getCanvasLink, getRelativeUrl, getWorkspaceLink, getWorkspaceUrl } from "utils/service";
import { capitalizeFirstLetter } from "utils/utils";
import "./ProjectTree.scss";
import { ParentReference, ProjectQuickAccessEntry } from "./interfaces";

interface MenuParentProps {
    recordId?: any;
    defaultProjectId: string;
    workspaceID: string;
    setPreviousActiveId: (activeId) => void;
    activeId: string | null;
    matchExecutionFilterPage: match<{}> | null;
    matchBundleManagerPage: match<{}> | null;
    matchTrashPage: match<{}> | null;
    isOpenedRecordDeleted: boolean;
    projectRefs: any;
    openState: { [id: string]: boolean };
    setOpenState: (value) => void;
    resource: Bundle | Project | Workspace;
    resourceType: "bundle" | "project" | "workspace";
    className?: string;
}
export const MenuParent = ({
    recordId,
    defaultProjectId,
    workspaceID,
    setPreviousActiveId,
    activeId,
    matchExecutionFilterPage: _matchExecutionFilterPage,
    matchBundleManagerPage,
    matchTrashPage,
    isOpenedRecordDeleted,
    projectRefs,
    openState,
    setOpenState,
    resource,
    resourceType,
    className,
}: MenuParentProps) => {
    const dispatch = useDispatch();
    const userPermissions = useSelector(getUserPermissions);
    const canUseStorageAnalysis = hasOrganizationAdminPermission(userPermissions);
    const history = useHistory();

    const location = useLocation();

    const [latestResourcesOpened, setLatestResourcesOpen] = useState(true);
    const [latestExecutionsOpened, setLatestExecutionsOpen] = useState(true);
    const latestResourcesRef = useRef<HTMLElement>(null);
    const latestExecutionsRef = useRef<HTMLElement>(null);

    const isContainerOpened = openState[resource.id];

    const openLatestListContainer = (latestListContainer: HTMLElement | null, setOpen) => {
        if (latestListContainer) {
            const hasActiveLink =
                latestListContainer.getElementsByClassName("record-label selected")?.length > 0;
            if (hasActiveLink) {
                setOpen(true);
            }
        }
    };

    useEffect(() => {
        openLatestListContainer(latestResourcesRef.current, setLatestResourcesOpen);
        openLatestListContainer(latestExecutionsRef.current, setLatestExecutionsOpen);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resource.pinned, resource.opened, resource.pinnedExecutions, resource.openedExecutions, location]);

    useEffect(() => {
        if (isContainerOpened) {
            setLatestResourcesOpen(true);
            setLatestExecutionsOpen(true);
        } else {
            setLatestResourcesOpen(false);
            setLatestExecutionsOpen(false);
        }
    }, [isContainerOpened]);

    const getProjectOrBundleAttr = () => {
        if (resourceType === "project") {
            return { project_id: resource.id };
        } else if (resourceType === "bundle") {
            if (resource.is_readonly) {
                return { project_id: defaultProjectId };
            } else {
                return { bundle_id: resource.id };
            }
        } else {
            return {};
        }
    };

    const emptyListPlaceholder =
        resourceType === "workspace" ? (
            "Open or pin some items to show them here"
        ) : (
            <>
                Open or pin some items to show them here or start by{" "}
                <button
                    type="button"
                    onClick={() => {
                        history.push(getNewRecordPath("flow", getProjectOrBundleAttr() as any));
                    }}
                >
                    creating a new flow.
                </button>
            </>
        );

    const checkActivePage = (link) =>
        isContainerNavigationLinkActive(link, location.pathname, location.search);

    const isItemCurrentlyViewed = (item: ProjectQuickAccessEntry) =>
        item.record_id_record.id === recordId || item.record_id_record.name === recordId;

    const onUnpinIconClick = (item: ProjectQuickAccessEntry) => {
        if (isItemCurrentlyViewed(item)) {
            return setPinned(dispatch, item.id, false);
        } else {
            return close(dispatch, history, item.id);
        }
    };

    const onCloseIconClick = (item: ProjectQuickAccessEntry, parent?: ParentReference) => {
        return close(dispatch, history, item.id, parent);
    };

    const executionFilterMenuItem = [
        {
            label: "Executions",
            icon: IconCollection.flashOutline,
            link:
                resourceType === "project" || resourceType === "bundle"
                    ? executionFilterPerLocationLink(
                          resourceType,
                          resource.name,
                          "name",
                          parentExecutionIdInitialFilter
                      )
                    : getWorkspaceExecutionFilterLink(parentExecutionIdInitialFilter)(resource.id),
            onClick: () => {
                setPreviousActiveId(activeId);
            },
        },
    ];
    const nonWorkspaceMenu = [
        {
            label: "Object Canvas",
            icon: IconCollection.vectorPolyLine,
            link: getCanvasLink(resourceType, resource.name, "template", "name"),
        },
        {
            label: `${capitalizeFirstLetter(resourceType)} Logs`,
            icon: IconCollection.alignRight,
            link: mapScreensToPaths.containerLogs(resourceType, resource.name, undefined, "name"),
        },
    ];
    const threeDotsWorkspaceMenu = [
        {
            label: "Advanced Search",
            icon: IconCollection.magnify,
            link: mapScreensToPaths.advancedSearch,
        },
        {
            label: "User & Role Management",
            icon: IconCollection.shieldAccountOutline,
            link: mapScreensToPaths.userRoleManagement,
        },
        {
            label: "Workspace Settings",
            icon: IconCollection.settingsIcon,
            link: mapScreensToPaths.workspaceSettings(workspaceID),
        },
        {
            label: "Workspace Logs",
            icon: IconCollection.alignRight,
            link: getWorkspaceLogsLink(workspaceID),
        },
        ...(canUseStorageAnalysis
            ? [
                  {
                      label: "Storage analysis",
                      icon: IconCollection.database,
                      link: getWorkspaceLink(workspaceID, "storage-analysis"),
                  },
              ]
            : []),
    ];
    const threeDotsMenu: {
        label: string;
        icon: IconLike;
        link: string;
        onClick?: () => void;
    }[] = resourceType === "workspace" ? threeDotsWorkspaceMenu : nonWorkspaceMenu;

    const latestListProps = {
        onUnpinIconClick,
        isItemCurrentlyViewed,
        resourceType,
        id: resource.id,
        onCloseIconClick,
    };
    const createLatestList = (props) => <LatestListContainer {...props} {...latestListProps} />;

    return (
        <SubMenu
            title={
                (
                    <RecordLabel
                        name={resource.name}
                        link={
                            resourceType === "workspace"
                                ? getWorkspaceUrl()
                                : getRelativeUrl(resourceType, resource.name, "name")
                        }
                        iconByType={resourceType}
                        image={resource.icon}
                        tooltipOverlay={getTooltipOverlay({
                            name: resource.name,
                            link:
                                resourceType === "workspace"
                                    ? getWorkspaceUrl()
                                    : getRelativeUrl(resourceType, resource.name, "name"),
                        })}
                        chip={
                            resourceType === "bundle" && (resource as Bundle).is_dirty ? (
                                <ChangedBundleBadge isDirty={(resource as Bundle).is_dirty} />
                            ) : null
                        }
                        scrollIntoView
                        tooltipOverlayClassName={propagateScrollToSidebar}
                    />
                ) as any
            }
            key={resource.id}
            className={`${className} ${
                activeId === resource.id ||
                (resourceType === "workspace" &&
                    !activeId &&
                    !matchBundleManagerPage &&
                    !matchTrashPage &&
                    !isOpenedRecordDeleted)
                    ? "highlightedContainer"
                    : ""
            }`}
            ref={(el: HTMLSpanElement) => (projectRefs.current[resource.id] = el)}
            open={openState[resource.id]}
            onOpenChange={(_) => {
                const newOpenState = { ...openState };
                newOpenState[resource.id] = true;
                setOpenState(newOpenState);
            }}
        >
            {[...executionFilterMenuItem, ...threeDotsMenu].map(
                ({ label, icon, link, onClick }) => {
                    const isActivePage = checkActivePage(link);
                    return (
                        <MenuItem key={label} className={`${isActivePage ? "activePageLink" : ""}`}>
                            <RecordLabel
                                name={label}
                                link={link}
                                icon={icon}
                                onClick={() => onClick?.()}
                                isSelectedManually={isActivePage}
                                scrollIntoView
                                tooltipOverlayClassName={propagateScrollToSidebar}
                            />
                        </MenuItem>
                    );
                }
            )}
            {isEmpty(resource[quickAccessPinned]) &&
            isEmpty(resource[quickAccessOpened]) &&
            isEmpty(resource[quickAccessExecutionsPinned]) &&
            isEmpty(resource[quickAccessExecutionsOpened]) ? (
                <div className="emptyProjectPlaceholder">
                    <Icon icon={IconCollection.packageVariant} />
                    <div className="text">{emptyListPlaceholder}</div>
                </div>
            ) : (
                <>
                    {createLatestList({
                        pinnedList: resource[quickAccessPinned],
                        openedList: resource[quickAccessOpened],
                        containerRef: latestResourcesRef,
                        containerLabel: "Last viewed & pinned resources",
                        isContainerOpened: latestResourcesOpened,
                        setContainerOpen: setLatestResourcesOpen,
                    })}
                    {createLatestList({
                        pinnedList: resource[quickAccessExecutionsPinned],
                        openedList: resource[quickAccessExecutionsOpened],
                        containerRef: latestExecutionsRef,
                        containerLabel: "Last viewed & pinned executions",
                        isContainerOpened: latestExecutionsOpened,
                        setContainerOpen: setLatestExecutionsOpen,
                    })}
                </>
            )}
        </SubMenu>
    );
};
